import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Carousel1 from "..//assets/images/carousel-1.jpg";
import Carousel2 from "..//assets/images/carousel-2.jpg";
import Carousel3 from "..//assets/images/carousel-3.jpg";
import Carousel4 from "..//assets/images/carousel-4.jpg";

const slides = [
  {
    image: Carousel1,
    title: "Elam Travels Trichy",
    subtitle: "Travel & Tour Operator",
    description:
      "Discover seamless travel solutions with our fleet of well-maintained vehicles. From car rentals to customized tour packages, Elam Travels Trichy ensures a hassle-free and unforgettable vacation experience!",
  },
  {
    image: Carousel2,
    title: "Tamil Nadu Temple Tour",
    subtitle: "Embark on a Sacred Journey",
    description:
      "Discover the spiritual heritage of Tamil Nadu with our expertly curated temple tours. Enjoy comfortable travel, priority temple access, and a serene pilgrimage experience. Let Elam Travels make your journey divine and stress-free!",
  },
  {
    image: Carousel3,
    title: "Plan Your Dream Holiday with Elam Travels!",
    subtitle: "Explore Top Destinations with Trusted Travel Experts",
    description:
      "Discover affordable and reliable tour and travel services in Trichy. Book luxury cars, tempo travelers, and personalized holiday packages effortlessly via phone or WhatsApp. Experience safe and memorable journeys with Elam Travels – Your Trusted Travel Partner!",
  },
  {
    image: Carousel4,
    title: "Hassle-Free Journey!",
    subtitle: "Car, Van and Bus Rentals",
    description:
      "Travel with ease by choosing from our wide range of vehicles, including Swift Dzire, Innova, and spacious 12-14 seater Tempo Travellers. Enjoy safe, affordable, and reliable rides with Elam Travels Trichy!",
  },
];

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const preloadImages = async () => {
      try {
        await Promise.all(
          slides.map(
            (slide) =>
              new Promise((resolve, reject) => {
                const img = new Image();
                img.src = slide.image;
                img.onload = resolve;
                img.onerror = reject;
              })
          )
        );
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading images:", error);
        setLoadError(true);
        setIsLoading(false);
      }
    };

    preloadImages();
  }, []);

  if (loadError) {
    return (
      <div className="h-[600px] bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-gray-600 mb-4">
            Unable to load images. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-orange-400 hover:bg-orange-500 text-black font-semibold py-3 rounded-lg transition p-4"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-[600px] bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <section
      id="home"
      className="relative h-[600px] overflow-hidden"
      aria-label="Hero Carousel"
    >
      <div aria-live="polite">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
            role="group"
            aria-hidden={index !== currentSlide}
            aria-label={`${slide.title} - ${slide.subtitle}`}
          >
            <div className="absolute inset-0 bg-black/50 z-10" />
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => setLoadError(true)}
            />
            <div className="absolute inset-0 z-20 flex items-center justify-center text-white text-center">
              <div className="container mx-auto px-4">
                <h1 className="text-white text-4xl md:text-6xl font-bold mb-4">
                  {slide.title}
                </h1>
                <h2 className="text-orange-100 text-2xl md:text-4xl mb-6">
                  {slide.subtitle}
                </h2>
                <p className="text-gray-200 max-w-2xl mx-auto text-lg mb-8">
                  {slide.description}
                </p>
                <button
                  className="bg-orange-400 hover:bg-orange-500 text-black font-semibold py-3 rounded-lg transition p-4"
                  onClick={() => scrollToSection("contact")}
                >
                  Enquire Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 p-2 rounded-full transition focus:outline-none focus:ring-2 focus:ring-white"
        aria-label="Previous slide"
        tabIndex={0}
        onKeyDown={(e) =>
          e.key === "Enter" || e.key === " " ? prevSlide() : null
        }
      >
        <ChevronLeft className="text-white" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 p-2 rounded-full transition focus:outline-none focus:ring-2 focus:ring-white"
        aria-label="Next slide"
        tabIndex={0}
        onKeyDown={(e) =>
          e.key === "Enter" || e.key === " " ? nextSlide() : null
        }
      >
        <ChevronRight className="text-white" size={24} />
      </button>
    </section>
  );
}
